/**
 * AppTable — wrapper sobre Polaris IndexTable.
 * Visual: idéntica a Polaris IndexTable hoy.
 * Beneficio: las páginas no importan directamente @shopify/polaris.
 * Migración futura: cambiar solo este archivo.
 */
import { IndexTable } from '@shopify/polaris';
import type { ReactNode } from 'react';
import type { IndexTableProps, IndexTableRowProps } from '@shopify/polaris';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AppTableBulkAction {
  content: string;
  onAction: () => void;
  /** Si true, Polaris pinta la acción en rojo (destructiva). */
  destructive?: boolean;
}

export interface AppTableProps {
  headings: { title: string; alignment?: 'start' | 'center' | 'end' }[];
  itemCount: number;
  selectable?: boolean;
  hideHeader?: boolean;
  selectedIds?: string[];
  selectableIds?: string[];
  onSelectAll?: (checked: boolean) => void;
  bulkActions?: AppTableBulkAction[];
  resourceName?: { singular: string; plural: string };
  /** Callback de selección en el formato de Polaris IndexTable */
  onSelectionChange?: IndexTableProps['onSelectionChange'];
  children?: ReactNode;
}

export interface AppTableRowProps {
  id: string;
  selected?: boolean;
  disabled?: boolean;
  selectable?: boolean;
  /** Tipo de fila Polaris: 'data' (default) | 'subheader' (grupo) | 'child'. */
  rowType?: IndexTableRowProps['rowType'];
  /** Rango [min,max] de posiciones que selecciona el checkbox del subheader (group-select). */
  selectionRange?: IndexTableRowProps['selectionRange'];
  position: number;
  /**
   * Click en toda la fila → abrir el detalle (paridad Shopify). Polaris
   * `IndexTable.Row.onClick` hace la fila clickable y respeta el checkbox de
   * selección. Los elementos interactivos de la fila (botones de acción,
   * checkbox) deben hacer `stopPropagation` para no doble-disparar.
   */
  onClick?: () => void;
  children: ReactNode;
}

export interface AppTableCellProps {
  children?: ReactNode;
  className?: string;
}

// ---------------------------------------------------------------------------
// AppTable.Row — wrapper sobre Polaris IndexTable.Row
// ---------------------------------------------------------------------------

function AppTableRow({ id, selected, disabled, selectable, rowType, selectionRange, position, onClick, children }: AppTableRowProps) {
  return (
    <IndexTable.Row
      id={id}
      selected={selected}
      disabled={disabled}
      hideSelectable={selectable === false}
      rowType={rowType}
      selectionRange={selectionRange}
      position={position}
      onClick={onClick}
    >
      {children}
    </IndexTable.Row>
  );
}

// ---------------------------------------------------------------------------
// AppTable.Cell — wrapper sobre Polaris IndexTable.Cell
// ---------------------------------------------------------------------------

function AppTableCell({ children, className }: AppTableCellProps) {
  return (
    <IndexTable.Cell className={className}>
      {children}
    </IndexTable.Cell>
  );
}

// ---------------------------------------------------------------------------
// AppTable (root) — wrapper sobre Polaris IndexTable
// ---------------------------------------------------------------------------

function AppTableRoot({
  headings,
  itemCount,
  selectable = false,
  selectedIds = [],
  selectableIds = [],
  onSelectAll,
  onSelectionChange,
  bulkActions = [],
  resourceName,
  children,
}: AppTableProps) {
  const selectedCount = selectedIds.length;
  const selectableCount = selectableIds.length;
  const allSelected = selectableCount > 0 && selectedCount >= selectableCount;
  const indeterminate = selectedCount > 0 && !allSelected;

  // Construimos el selectedItemsCount que Polaris espera
  const selectedItemsCount: IndexTableProps['selectedItemsCount'] = selectable
    ? (allSelected ? 'All' : selectedCount)
    : undefined;

  // Polaris maneja su propio onSelectionChange internamente si se pasa
  const handleSelectionChange: IndexTableProps['onSelectionChange'] = onSelectionChange
    ?? ((selectionType, toggleType, selection) => {
      if (!onSelectAll) return;
      if (selectionType === 'page' || selectionType === 'all') {
        onSelectAll(toggleType);
      }
    });

  const promotedBulkActions = bulkActions.map((ba) => ({
    content: ba.content,
    onAction: ba.onAction,
    ...(ba.destructive ? { destructive: true } : {}),
  }));

  return (
    <IndexTable
      resourceName={resourceName}
      itemCount={itemCount}
      selectedItemsCount={selectedItemsCount}
      onSelectionChange={selectable ? handleSelectionChange : undefined}
      headings={headings as IndexTableProps['headings']}
      promotedBulkActions={selectable && promotedBulkActions.length ? promotedBulkActions : undefined}
      selectable={selectable}
    >
      {children}
    </IndexTable>
  );
}

// ---------------------------------------------------------------------------
// Compound export
// ---------------------------------------------------------------------------

export const AppTable = Object.assign(AppTableRoot, {
  Row: AppTableRow,
  Cell: AppTableCell,
});
